import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function PrivacyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Privacy Policy - VisionSet',
    description: 'VisionSet Privacy Policy. Learn how we collect, use, and protect your personal data. Information about data storage, retention, security, and your privacy rights.',
    keywords: 'privacy policy, data protection, GDPR, CCPA, privacy rights',
    canonical: '/privacy',
    ogTitle: 'Privacy Policy - VisionSet',
    ogDescription: 'VisionSet Privacy Policy. Learn how we collect, use, and protect your personal data.',
    ogUrl: '/privacy',
    twitterTitle: 'Privacy Policy - VisionSet',
    twitterDescription: 'Learn how VisionSet protects your privacy and personal data.',
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Privacy Policy",
      "url": `${siteUrl}/privacy`,
      "description": "VisionSet Privacy Policy - How we collect, use, and protect your personal information.",
      "publisher": {
        "@type": "Organization",
        "name": "Ausonio Algorithmics LLC",
        "url": "https://www.ausonioalg.com/"
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://static.readdy.ai/image/4126e18b1195bdf88076d12182861244/e0a391f1061e2e4527f1f332a6fc5bcf.png" alt="VisionScout" className="h-10" />
          </Link>
          
          <Link to="/" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
        <p className="text-slate-600 mb-12">Last updated: January 2026</p>

        <div className="space-y-10 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">1. Company Information</h2>
            <p className="mb-4">
              VisionScout is operated by <strong>Ausonio Algorithmics LLC</strong>, a limited liability company organized under the laws of the State of Colorado, United States.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Company Name:</strong> Ausonio Algorithmics LLC</li>
              <li><strong>State of Organization:</strong> Colorado</li>
              <li><strong>Contact Email:</strong> support@VisionScout.com</li>
              <li><strong>Privacy Inquiries:</strong> privacy@VisionScout.com</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">2. Introduction</h2>
            <p className="mb-4">
              Welcome to VisionScout. We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">3. Information We Collect</h2>
            <p className="mb-4">
              We collect several types of information to provide and improve our Service:
            </p>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.1 Account Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Email address</li>
              <li>Name (if provided)</li>
              <li>Password hashes (we do not store plain-text passwords)</li>
              <li>Account preferences and settings</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.2 Usage Data</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Search queries and dataset descriptions</li>
              <li>Images curated (kept or rejected)</li>
              <li>Project information and metadata</li>
              <li>Credit usage and billing history</li>
              <li>Feature usage patterns</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.3 Image-Related Data</h3>
            <p className="mb-4">
              We store the following image-related information:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Image URLs (links to source images)</li>
              <li>Image thumbnails and previews (cached temporarily for display)</li>
              <li>Keep/reject decisions for each image in your swipe pool</li>
              <li>Exported ZIP files (stored for 7 days after export, then automatically deleted)</li>
              <li>Image metadata (dimensions, source information)</li>
            </ul>
            <p className="mt-4">
              <strong>Important:</strong> We do not host or own the original images. We cache thumbnails temporarily to provide the Service. Images and thumbnails expire according to your plan tier (7 days for Free, 1 month for Starter, indefinitely for Pro while account is active).
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.4 Technical Data</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>IP address (logged automatically for security and fraud prevention)</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Operating system</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.5 Payment Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Billing address</li>
              <li>Payment method details (processed securely by Stripe; we do not store full card numbers)</li>
              <li>Transaction history</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">4. How We Use Your Information</h2>
            <p className="mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>To provide and maintain the Service</li>
              <li>To process your dataset creation requests</li>
              <li>To manage your account and subscription</li>
              <li>To process payments and prevent fraud</li>
              <li>To improve and optimize the Service</li>
              <li>To provide customer support</li>
              <li>To send service-related notifications</li>
              <li>To analyze usage patterns and trends</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">5. Data Storage and Retention</h2>
            <p className="mb-4">
              We store your data securely and retain it according to the following policies:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Account information: Retained while your account is active and for a reasonable period after deletion</li>
              <li>Project data and image URLs: Retained according to your subscription plan (7 days for Free, 1 month for Starter, indefinitely for Pro)</li>
              <li>Cached thumbnails: Expire according to the same schedule as project data</li>
              <li>Exported ZIP files: Stored for 7 days after export, then automatically deleted</li>
              <li>Usage data: Retained for analytical purposes for up to 2 years</li>
              <li>Payment records and credit ledgers: Retained as required by law (typically 7 years for tax and auditing purposes)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">6. Third-Party Service Providers</h2>
            <p className="mb-4">
              We share data with the following third-party service providers who help us operate the Service:
            </p>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.1 Payment Processing</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Stripe:</strong> Processes all payment transactions securely. Stripe receives your billing information and payment details.</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.2 Hosting and Infrastructure</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Cloud hosting providers:</strong> Store application data, cached images, and user content</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.3 Analytics (if applicable)</h3>
            <p className="mb-4">
              We may use analytics services to understand how users interact with the Service. These services may collect:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Page views and navigation patterns</li>
              <li>Feature usage statistics</li>
              <li>Aggregated, anonymized usage data</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.4 Customer Support Tools</h3>
            <p className="mb-4">
              We may use customer support platforms to respond to your inquiries and provide assistance.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">7. Data Sharing and Disclosure</h2>
            <p className="mb-4">
              We do not sell your personal data. We may share your information only in the following circumstances:
            </p>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">7.1 Service Providers</h3>
            <p className="mb-4">
              As described in Section 6, we share data with trusted third-party service providers who are contractually obligated to protect your information.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">7.2 Legal Requirements</h3>
            <p className="mb-4">
              We may disclose your information if required by law or in response to valid legal requests, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Compliance with court orders or subpoenas</li>
              <li>Protection of our legal rights</li>
              <li>Investigation of fraud or security issues</li>
              <li>Protection of user safety</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">7.3 Business Transfers</h3>
            <p className="mb-4">
              If VisionScout is involved in a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction. We will notify you before your data is transferred and becomes subject to a different privacy policy.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">8. Cookies and Tracking Technologies</h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies to provide and improve the Service:
            </p>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">8.1 Essential Cookies</h3>
            <p className="mb-4">
              Required for the Service to function properly:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Session management (keeping you logged in)</li>
              <li>Authentication and security</li>
              <li>Remembering your preferences</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">8.2 Analytics Cookies (if applicable)</h3>
            <p className="mb-4">
              Help us understand how users interact with the Service:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Page views and navigation patterns</li>
              <li>Feature usage statistics</li>
              <li>Performance monitoring</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">8.3 Your Cookie Choices</h3>
            <p className="mb-4">
              You can control cookies through your browser settings. However, disabling essential cookies may affect Service functionality. We do not use cookies for marketing or advertising purposes.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">8.4 Do Not Track</h3>
            <p className="mb-4">
              We do not track users across third-party websites. We do not respond to Do Not Track (DNT) signals because we do not engage in cross-site tracking.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">9. Data Security</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational measures to protect your data:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication</li>
              <li>Secure payment processing through Stripe</li>
              <li>Regular backups</li>
              <li>Password hashing (we never store plain-text passwords)</li>
            </ul>
            <p className="mt-4">
              However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">10. Your Rights</h2>
            <p className="mb-4">
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your data</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Objection:</strong> Object to certain data processing activities</li>
              <li><strong>Restriction:</strong> Request restriction of data processing</li>
              <li><strong>Withdrawal:</strong> Withdraw consent for data processing</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at privacy@VisionScout.com or support@VisionScout.com.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">11. Account Deletion and Data Export</h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">11.1 Account Deletion</h3>
            <p className="mb-4">
              When you delete your account:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>All projects, image URLs, and curated datasets are permanently deleted within 30 days</li>
              <li>Cached thumbnails and previews are deleted within 30 days</li>
              <li>Previously exported ZIP files remain available for download for 7 days, then are permanently deleted</li>
              <li>Account information is anonymized or deleted within 90 days</li>
              <li>We retain transaction records and credit ledgers for 7 years for tax and auditing purposes (as required by law)</li>
              <li>Anonymized usage analytics may be retained indefinitely</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">11.2 Data Export</h3>
            <p className="mb-4">
              You may export your data at any time:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Project export functionality (download curated images as ZIP files)</li>
              <li>Account data export request (contact support@VisionScout.com for a copy of your account information)</li>
            </ul>
            <p className="mt-4">
              Note: We do not provide exports of cached thumbnails or temporary data, only your curated datasets and account information.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">12. Children's Privacy</h2>
            <p className="mb-4">
              VisionScout is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately at privacy@VisionScout.com.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">13. International Data Transfers</h2>
            <p className="mb-4">
              VisionScout is operated from the United States. Your data may be transferred to, stored, and processed in the United States and other countries where our service providers operate. These countries may have data protection laws that differ from those in your country of residence.
            </p>
            <p className="mt-4">
              By using the Service, you consent to the transfer of your data to the United States and other countries. We ensure appropriate safeguards are in place to protect your data in accordance with this privacy policy and applicable laws.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">14. Third-Party Links</h2>
            <p className="mb-4">
              The Service may contain links to third-party websites or display images from external sources. We are not responsible for the privacy practices of these external sites or content providers. We encourage you to review their privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">15. Changes to This Privacy Policy</h2>
            <p className="mb-4">
              We may update this privacy policy from time to time. When we make changes, we will:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Post the new policy on this page</li>
              <li>Update the "Last updated" date</li>
              <li>For significant changes, we may send email notification to registered users</li>
            </ul>
            <p className="mt-4">
              Your continued use of the Service after changes become effective constitutes acceptance of the updated policy. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">16. Contact Us</h2>
            <p className="mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>General Inquiries:</strong> support@VisionScout.com</li>
              <li><strong>Privacy Inquiries:</strong> privacy@VisionScout.com</li>
              <li><strong>Company:</strong> Ausonio Algorithmics LLC</li>
              <li><strong>State:</strong> Colorado, United States</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">17. GDPR Compliance (EU Users)</h2>
            <p className="mb-4">
              If you are located in the European Economic Area (EEA), we process your data based on the following legal grounds:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Contract performance:</strong> To provide the Service you signed up for</li>
              <li><strong>Legitimate interests:</strong> To improve and secure the Service, prevent fraud</li>
              <li><strong>Legal obligations:</strong> To comply with applicable laws and regulations</li>
              <li><strong>Consent:</strong> Where explicitly provided by you</li>
            </ul>
            <p className="mt-4">
              You have the right to lodge a complaint with your local data protection authority if you believe we have not complied with applicable data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">18. CCPA Compliance (California Users)</h2>
            <p className="mb-4">
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Right to know:</strong> What personal information is collected, used, shared, or sold</li>
              <li><strong>Right to delete:</strong> Request deletion of your personal information</li>
              <li><strong>Right to opt-out:</strong> Opt-out of the sale of personal information (Note: We do not sell your personal information)</li>
              <li><strong>Right to non-discrimination:</strong> We will not discriminate against you for exercising your CCPA rights</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact us at privacy@VisionScout.com. We will verify your identity before processing your request.
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-200">
          <Link to="/" className="text-blue-600 font-medium hover:text-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-slate-400">
            <p>&copy; 2025 VisionScout. All rights reserved.</p>
            <p>Built by <a href="https://www.ausonioalg.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Ausonio Algorithmics</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
